import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { GitBranch, Plus, ChevronLeft, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { mfgAPI } from '../../services/api';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import RecordActions from '../../components/common/RecordActions';
import RecordViewDialog from '../../components/common/RecordViewDialog';

const CONTROL_KEYS = ['PP01', 'PP02', 'PP03'];

const Routings = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [rows, setRows] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [plants, setPlants] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [workCenters, setWorkCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [header, setHeader] = useState({ material_id: '', plant_id: '', description: '', base_quantity: 1, base_uom: '', status: 'ACTIVE' });
  const [operations, setOperations] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [rtRes, matRes, plantRes, uomRes, wcRes] = await Promise.all([
        mfgAPI.getRoutings(), mfgAPI.getMaterials(), mfgAPI.getPlants(), mfgAPI.getUOMs(), mfgAPI.getWorkCenters(),
      ]);
      setRows(rtRes.data); setMaterials(matRes.data); setPlants(plantRes.data); setUoms(uomRes.data); setWorkCenters(wcRes.data);
    } catch (e) { toast.error('Failed to load routings'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setHeader({ material_id: '', plant_id: '', description: '', base_quantity: 1, base_uom: '', status: 'ACTIVE' });
    setOperations([{ operation_no: 10, work_center_id: '', description: '', control_key: 'PP01', setup_time: 0, machine_time: 0, labor_time: 0, time_uom: 'MIN' }]);
    setOpen(true);
  };
  const openEdit = (rt) => {
    setEditing(rt);
    setHeader({ material_id: rt.material_id, plant_id: rt.plant_id, description: rt.description, base_quantity: rt.base_quantity, base_uom: rt.base_uom, status: rt.status });
    setOperations(rt.operations.map((o) => ({ ...o })));
    setOpen(true);
  };

  const addOp = () => {
    const nextNo = operations.length ? Math.max(...operations.map((o) => o.operation_no)) + 10 : 10;
    setOperations([...operations, { operation_no: nextNo, work_center_id: '', description: '', control_key: 'PP01', setup_time: 0, machine_time: 0, labor_time: 0, time_uom: 'MIN' }]);
  };
  const setOp = (idx, k, v) => setOperations(operations.map((o, i) => (i === idx ? { ...o, [k]: v } : o)));
  const removeOp = (idx) => setOperations(operations.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!header.material_id || !header.plant_id || !header.base_uom || !header.description) {
      toast.error('Material, Plant, Description and Base UoM are required'); return;
    }
    const validOps = operations.filter((o) => o.work_center_id && o.description);
    if (validOps.length === 0) { toast.error('Add at least one operation'); return; }
    const payload = {
      ...header, base_quantity: Number(header.base_quantity),
      operations: validOps.map((o) => ({
        operation_no: Number(o.operation_no), work_center_id: o.work_center_id, description: o.description,
        control_key: o.control_key, setup_time: Number(o.setup_time || 0), machine_time: Number(o.machine_time || 0),
        labor_time: Number(o.labor_time || 0), time_uom: o.time_uom || 'MIN', base_quantity: 1,
      })),
    };
    try {
      if (editing) { await mfgAPI.updateRouting(editing.routing_id, payload); toast.success('Routing updated'); }
      else { await mfgAPI.createRouting(payload); toast.success('Routing created'); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (rt) => {
    if (!window.confirm(`Delete routing ${rt.routing_id}?`)) return;
    try { await mfgAPI.deleteRouting(rt.routing_id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };
  void remove;

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="routings-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/manufacturing')}><ChevronLeft className="h-5 w-5" /></Button>
            <div className="p-2 bg-orange-600 rounded-lg"><GitBranch className="h-5 w-5 text-white" /></div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Routings</h1>
              <p className="text-sm text-slate-500">Operation sequences on work centers</p>
            </div>
          </div>
          {canWrite('routing') && (<Button onClick={openCreate} className="bg-orange-600 hover:bg-orange-700" data-testid="create-routing-btn"><Plus className="h-4 w-4 mr-2" /> Add Routing</Button>)}
        </div>

        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="font-semibold">Routing</TableHead>
                  <TableHead className="font-semibold">Material</TableHead>
                  <TableHead className="font-semibold">Plant</TableHead>
                  <TableHead className="font-semibold">Description</TableHead>
                  <TableHead className="font-semibold text-center">Operations</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">Loading…</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">No routings yet</TableCell></TableRow>
                ) : rows.map((rt) => (
                  <TableRow key={rt.routing_id} data-testid={`routing-row-${rt.routing_id}`} className="hover:bg-orange-50/40">
                    <TableCell className="font-mono font-medium text-orange-600">{rt.routing_id}</TableCell>
                    <TableCell>{rt.material_id}</TableCell>
                    <TableCell>{rt.plant_id}</TableCell>
                    <TableCell>{rt.description}</TableCell>
                    <TableCell className="text-center">{rt.operations?.length || 0}</TableCell>
                    <TableCell><Badge className={rt.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700 border-0' : 'bg-slate-200 text-slate-600 border-0'}>{rt.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      <RecordActions
                        doctype="routing"
                        idForTestId={rt.routing_id}
                        onView={() => setViewItem(rt)}
                        onEdit={() => openEdit(rt)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? `Edit Routing ${editing.routing_id}` : 'Create Routing'}</DialogTitle>
              <DialogDescription>Define the operation sequence and the work centers that perform each step.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-3 gap-4 py-2">
              <div>
                <Label>Material *</Label>
                <Select value={header.material_id} onValueChange={(v) => setHeader({ ...header, material_id: v })} disabled={!!editing}>
                  <SelectTrigger data-testid="rt-material-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{materials.map((m) => <SelectItem key={m.material_number} value={m.material_number}>{m.material_number} — {m.description}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Plant *</Label>
                <Select value={header.plant_id} onValueChange={(v) => setHeader({ ...header, plant_id: v })} disabled={!!editing}>
                  <SelectTrigger data-testid="rt-plant-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{plants.map((p) => <SelectItem key={p.plant_id} value={p.plant_id}>{p.plant_id}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Base UoM *</Label>
                <Select value={header.base_uom} onValueChange={(v) => setHeader({ ...header, base_uom: v })}>
                  <SelectTrigger data-testid="rt-baseuom-select"><SelectValue placeholder="UoM" /></SelectTrigger>
                  <SelectContent>{uoms.map((u) => <SelectItem key={u.unit_code} value={u.unit_code}>{u.unit_code}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Description *</Label>
                <Input data-testid="rt-desc-input" value={header.description} onChange={(e) => setHeader({ ...header, description: e.target.value })} />
              </div>
              <div>
                <Label>Base Quantity</Label>
                <Input type="number" value={header.base_quantity} onChange={(e) => setHeader({ ...header, base_quantity: e.target.value })} />
              </div>
            </div>

            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-slate-800">Operations</h3>
                <Button size="sm" variant="outline" onClick={addOp} data-testid="add-operation-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add Operation</Button>
              </div>
              <div className="space-y-2">
                {operations.map((o, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`operation-row-${idx}`}>
                    <div className="col-span-1">
                      <Label className="text-xs">Op</Label>
                      <Input type="number" value={o.operation_no} onChange={(e) => setOp(idx, 'operation_no', e.target.value)} />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">Work Center</Label>
                      <Select value={o.work_center_id} onValueChange={(v) => setOp(idx, 'work_center_id', v)}>
                        <SelectTrigger data-testid={`op-wc-select-${idx}`}><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{workCenters.map((wc) => <SelectItem key={wc.work_center_id} value={wc.work_center_id}>{wc.work_center_id}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">Description</Label>
                      <Input value={o.description} onChange={(e) => setOp(idx, 'description', e.target.value)} />
                    </div>
                    <div className="col-span-1">
                      <Label className="text-xs">Setup</Label>
                      <Input type="number" value={o.setup_time} onChange={(e) => setOp(idx, 'setup_time', e.target.value)} />
                    </div>
                    <div className="col-span-1">
                      <Label className="text-xs">Machine</Label>
                      <Input type="number" value={o.machine_time} onChange={(e) => setOp(idx, 'machine_time', e.target.value)} />
                    </div>
                    <div className="col-span-1">
                      <Label className="text-xs">Labor</Label>
                      <Input type="number" value={o.labor_time} onChange={(e) => setOp(idx, 'labor_time', e.target.value)} />
                    </div>
                    <div className="col-span-1">
                      <Label className="text-xs">CKey</Label>
                      <Select value={o.control_key} onValueChange={(v) => setOp(idx, 'control_key', v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{CONTROL_KEYS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-1">
                      <Button size="icon" variant="ghost" className="text-red-500" onClick={() => removeOp(idx)}><X className="h-4 w-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} className="bg-orange-600 hover:bg-orange-700" data-testid="save-routing-btn">{editing ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <RecordViewDialog
          open={!!viewItem}
          onOpenChange={(o) => { if (!o) setViewItem(null); }}
          title={viewItem ? `Routing ${viewItem.routing_id}` : 'Routing'}
          record={viewItem}
        />
      </div>
    </Layout>
  );
};

export default Routings;
